<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  STATUS_LABELS,
  createSupplier,
  deleteSupplier,
  listSuppliers,
  updateSupplier,
  type Supplier,
  type SupplierInput,
} from '../api/suppliers'

const rows = ref<Supplier[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', status: '', page: 1, size: 20 })

const formOpen = ref(false)
const editing = ref<Supplier | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive<SupplierInput>({ code: '', name: '', contact: '', phone: '', status: 'active' })

const codeError = computed(() => (form.code.trim() ? '' : '请输入供应商编码'))

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listSuppliers({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => void load())

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function openCreate(): void {
  editing.value = null
  Object.assign(form, { code: '', name: '', contact: '', phone: '', status: 'active' })
  formError.value = null
  formOpen.value = true
}

function openEdit(row: Supplier): void {
  editing.value = row
  Object.assign(form, {
    code: row.code,
    name: row.name,
    contact: row.contact,
    phone: row.phone,
    status: row.status,
  })
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (codeError.value) return
  saving.value = true
  formError.value = null
  const input: SupplierInput = {
    code: form.code.trim(),
    name: form.name.trim(),
    contact: form.contact.trim(),
    phone: form.phone.trim(),
    status: form.status,
  }
  try {
    if (editing.value) await updateSupplier(editing.value.id, input)
    else await createSupplier(input)
    formOpen.value = false
    await load(editing.value ? '供应商已更新' : '供应商已创建')
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: Supplier): Promise<void> {
  notice.value = ''
  try {
    await deleteSupplier(row.id)
    await load(`已删除供应商「${row.code}」`)
  } catch (e) {
    error.value = e
  }
}
</script>

<template>
  <div class="yc-sup">
    <header class="yc-sup__bar">
      <el-input
        v-model="query.keyword"
        class="yc-sup__keyword"
        placeholder="编码 / 名称"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.status" class="yc-sup__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'wms:supplier:create'" type="primary" @click="openCreate">新建供应商</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-sup__notice">{{ notice }}</span>
    </header>

    <div v-if="error" class="yc-sup__error">
      <TraceIdAlert :error="error" />
    </div>

    <el-card v-loading="loading" class="yc-sup__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="code" label="供应商编码" min-width="140" />
        <el-table-column prop="name" label="名称" min-width="180" show-overflow-tooltip />
        <el-table-column prop="contact" label="联系人" width="120" />
        <el-table-column prop="phone" label="电话" min-width="150" show-overflow-tooltip />
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="row.status === 'active' ? 'success' : 'info'" size="small">
              {{ STATUS_LABELS[row.status as Supplier['status']] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="160">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template #default="{ row }">
            <el-button v-perm="'wms:supplier:update'" link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-popconfirm title="确认删除该供应商？" width="200" @confirm="remove(row)">
              <template #reference>
                <el-button v-perm="'wms:supplier:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有供应商"
        description="入库单要选供应商，先建一个常用的。"
      >
        <el-button v-perm="'wms:supplier:create'" type="primary" @click="openCreate">新建供应商</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-sup__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑供应商 · ${editing.code}` : '新建供应商'"
      width="480px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-sup__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="供应商编码" :error="codeError">
          <el-input v-model="form.code" maxlength="32" placeholder="如 SUP001" />
        </el-form-item>

        <el-form-item label="名称">
          <el-input v-model="form.name" maxlength="64" placeholder="如 华东纸箱厂" />
        </el-form-item>

        <el-form-item label="联系人">
          <el-input v-model="form.contact" maxlength="32" placeholder="如 张三" />
        </el-form-item>

        <el-form-item label="电话">
          <el-input v-model="form.phone" maxlength="32" placeholder="如 13800138000" />
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio value="active">启用</el-radio>
            <el-radio value="disabled">停用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!codeError">
          {{ editing ? '保存' : '创建' }}
        </el-button>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-sup {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 200px;
  }

  &__status {
    width: 140px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }
}
</style>
